import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "axios";
import API_BASE from "../config/api";
import { auth } from "../config/config-firebase";
import useMerchantStores, {
  isDefaultStoreRow,
  ACTIVE_CUSTOMER_ID_CHANGED_EVENT,
} from "../hooks/useMerchantStores";
import {
  isAdminMerchantMode,
  getActiveMerchantCustomerId,
  isPromoLoginMode,
} from "../utils/adminMerchantMode";
import { DEFAULT_DESIGN_OPTIONS } from "./MenuPreviewPanel";
import { buildItemFromForm } from "../utils/menuItem";
import {
  sanitizeDesignOptionsForSave,
  cacheDesignOptions,
  loadCachedDesignOptions,
} from "../utils/menuDesignOptions";
import {
  saveItemImage as saveItemImageRequest,
  removeItemImage as removeItemImageRequest,
  uploadItemMedia as uploadItemMediaRequest,
  listItemImages,
} from "../components/workflow/imageCreationService";

// Merges each item's saved AI-generated image (from product_upc_images, via
// listItemImages) onto freshly-loaded sections. product_upc_images is the
// source of truth for item images -- not menu_data -- so this always
// overwrites item.image, including clearing a stale one from old cached
// data that predates this table.
async function attachSavedItemImages(sections) {
  const itemIds = (sections || []).flatMap((section) =>
    (section.items || []).map((item) => item.id),
  );
  let imagesByItemId = {};
  try {
    imagesByItemId = await listItemImages(itemIds);
  } catch (err) {
    console.error("[useAIMenu] listItemImages failed:", err.message);
    return sections; // keep going with whatever items already have -- non-fatal
  }
  return (sections || []).map((section) => ({
    ...section,
    items: (section.items || []).map((item) => ({
      ...item,
      image: imagesByItemId[item.id] || null,
    })),
  }));
}

const normalizeSectionName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase();

/** Same menu line = same normalized name + price (avoids duplicate rows when re-extracting). */
const itemDedupKey = (item) => {
  const name = String(item?.name || "").trim().toLowerCase();
  const raw = String(item?.price ?? "").trim().replace(/[^0-9.]/g, "");
  const num = parseFloat(raw);
  const priceKey = Number.isFinite(num) ? num.toFixed(2) : raw || "0.00";
  return `${name}::${priceKey}`;
};

const dedupeItemsInSection = (items) => {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const k = itemDedupKey(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
};

const appendItemsWithoutDuplicates = (existingItems, incomingItems) => {
  const keys = new Set((existingItems || []).map(itemDedupKey));
  const toAdd = [];
  for (const item of incomingItems || []) {
    const k = itemDedupKey(item);
    if (keys.has(k)) continue;
    keys.add(k);
    toAdd.push(item);
  }
  return [...(existingItems || []), ...toAdd];
};

const createLocalId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const formatPrice = (value) => {
  if (!value) return "—";
  const str = value.toString().trim();
  const num = Number(str);
  return !isNaN(num) && str !== "" ? `$${num.toFixed(2)}` : str;
};

const PRICE_HEADING_PATTERNS = [
  "on roll",
  "per lb",
  "per lbs",
  "per kg",
  "per plate",
  "per piece",
  "each",
  "ea",
];

const escapeRegex = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const titleCaseHeading = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const extractPriceHeading = (item) => {
  const explicitHeading = String(item?.priceHeading || "").trim();
  if (explicitHeading) return titleCaseHeading(explicitHeading);

  const fromPrice = String(item?.price || "").trim();
  const fromDescription = String(item?.description || "").trim();
  const searchText = `${fromPrice} ${fromDescription}`.toLowerCase();

  for (const pattern of PRICE_HEADING_PATTERNS) {
    const matcher = new RegExp(`\\b${escapeRegex(pattern)}\\b`, "i");
    if (matcher.test(searchText)) {
      return titleCaseHeading(pattern);
    }
  }

  return "";
};

const stripPriceHeadingFromDescription = (item) => {
  const rawDescription = String(item?.description || "").trim();
  if (!rawDescription) return "—";

  const heading = extractPriceHeading(item);
  if (!heading) return rawDescription;

  const matcher = new RegExp(
    `(^|\\s|,|\\-|:)${escapeRegex(heading)}(?=\\s|,|\\-|:|$)`,
    "ig",
  );
  const cleaned = rawDescription.replace(matcher, " ").replace(/\s{2,}/g, " ").trim();
  return cleaned || "—";
};

const useAIMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isJsonPreviewOpen, setIsJsonPreviewOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [uid, setUid] = useState(null);
  const [showEditorMobile, setShowEditorMobile] = useState(false);
  const [designOptions, setDesignOptions] = useState(DEFAULT_DESIGN_OPTIONS);
  const [previewMode, setPreviewMode] = useState("json");
  const [extractedSectionsOnly, setExtractedSectionsOnly] = useState([]);
  const skipNextFetchRef = useRef(false);
  const hasShownCloudLoadedMessageRef = useRef(false);
  const designOptionsRef = useRef(designOptions);
  const lastAutoFetchedCustomerIdRef = useRef(null);
  const syncMenuToServerRef = useRef(null);
  const extractMergeKeyRef = useRef(null);

  // The AI Menu is scoped by the active store's own customer_id (see
  // getMerchantStoreDetails — each store row carries its own customer_id).
  // Resolve the current uid the same way StoreListPage/StoreSwitcher do,
  // then read the default store's customer_id off the shared hook instead
  // of trusting a possibly-stale localStorage "customer_id".
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUid(currentUser?.uid || localStorage.getItem("customer_id") || null);
    });
    return () => unsubscribe();
  }, []);

  // Admin Merchant Mode: use the merchant customer_id the admin selected in
  // LoginAsMerchant directly, bypassing the Firebase-uid-based store hook
  // below (which never applies to an admin session).
  //
  // Promo Login Mode: same situation — a promo device links via
  // customerId + phone (PromoLogin.js) and never signs into Firebase, so
  // there's no real uid for the hook to resolve either (see Mode.js).
  const adminMerchantMode = isAdminMerchantMode();
  const promoLoginMode = isPromoLoginMode();
  const bypassStoreHook = adminMerchantMode || promoLoginMode;
  const { stores, loadingStores: loadingStoresRaw, fetchStores } = useMerchantStores(
    bypassStoreHook ? null : uid,
  );
  const activeStore = stores.find(isDefaultStoreRow) || stores[0] || null;

  // Promo Login Mode has no `stores`/`activeStore` of its own (the hook is
  // bypassed above), so its customerId has to come from localStorage
  // instead — but reading it inline on every render isn't reactive: it
  // won't pick up a switch made via StoreSwitcher (a sibling component,
  // same page) until something re-renders this hook's owner. Track it as
  // state instead, seeded from localStorage and kept in sync by the
  // ACTIVE_CUSTOMER_ID_CHANGED_EVENT listener underneath.
  const [promoCustomerId, setPromoCustomerId] = useState(() => localStorage.getItem("customer_id"));
  const customerId = adminMerchantMode
    ? getActiveMerchantCustomerId()
    : promoLoginMode
    ? promoCustomerId
    : (activeStore?.customer_id || null);
  const loadingStores = bypassStoreHook ? false : loadingStoresRaw;

  // StoreSwitcher (rendered by AIMenuScreen) uses its OWN useMerchantStores
  // instance, so switching the default store there doesn't touch this
  // hook's copy of `stores` — without this, the newly-active store's menu
  // only shows up after a full page reload. Refetch this instance's stores
  // whenever a switch happens anywhere on the page. In Promo Login Mode
  // `fetchStores` is a no-op (the hook was never given a uid to work with),
  // so update `promoCustomerId` directly instead — that's what actually
  // drives `customerId` for this mode.
  useEffect(() => {
    if (adminMerchantMode) return;
    const handleActiveStoreChanged = (event) => {
      if (promoLoginMode) {
        setPromoCustomerId(event?.detail?.customerId || localStorage.getItem("customer_id"));
        return;
      }
      fetchStores();
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
  }, [adminMerchantMode, promoLoginMode, fetchStores]);

  const ensureItemsWithVisibility = useCallback((inputSections) => {
    const usedSectionIds = new Set();

    return (inputSections || []).map((section) => {
      let sectionId = String(section?.id || "").trim() || createLocalId();
      while (usedSectionIds.has(sectionId)) {
        sectionId = createLocalId();
      }
      usedSectionIds.add(sectionId);

      const usedItemIds = new Set();
      const normalizedItems = (section?.items || []).map((item) => {
        let itemId = String(item?.id || "").trim() || createLocalId();
        while (usedItemIds.has(itemId)) {
          itemId = createLocalId();
        }
        usedItemIds.add(itemId);

        return {
          ...item,
          id: itemId,
          isVisible: item?.isVisible !== false,
        };
      });

      return {
        ...section,
        id: sectionId,
        items: normalizedItems,
      };
    });
  }, []);

  const mergeExtractedIntoSections = useCallback(
    (baseSections, extractedSections) => {
      const merged = Array.isArray(baseSections) ? [...baseSections] : [];
      (extractedSections || []).forEach((incomingSection) => {
        const incomingName = String(incomingSection?.name || "").trim();
        if (!incomingName) return;

        const matchIndex = merged.findIndex(
          (section) =>
            normalizeSectionName(section?.name) === normalizeSectionName(incomingName),
        );

        const incomingItems = dedupeItemsInSection(
          Array.isArray(incomingSection?.items)
            ? incomingSection.items
                .filter((item) => String(item?.name || "").trim())
                .map((item) => ({ ...item, isVisible: item?.isVisible !== false }))
            : [],
        );

        if (matchIndex >= 0) {
          const targetSection = merged[matchIndex];
          merged[matchIndex] = {
            ...targetSection,
            items: appendItemsWithoutDuplicates(targetSection?.items || [], incomingItems),
          };
        } else {
          merged.push({
            id: incomingSection?.id || Date.now().toString(),
            name: incomingName,
            items: incomingItems,
          });
        }
      });
      return ensureItemsWithVisibility(merged);
    },
    [ensureItemsWithVisibility],
  );

  const getFirstMergedTargetSectionId = useCallback((mergedSections, extractedSections) => {
    const firstTargetName = (extractedSections || []).find(
      (section) => String(section?.name || "").trim(),
    )?.name;
    if (!firstTargetName) return null;

    return (
      mergedSections.find(
        (section) =>
          normalizeSectionName(section?.name) === normalizeSectionName(firstTargetName),
      )?.id || null
    );
  }, []);

  const fetchCustomerMenu = useCallback(
    async (id) => {
      if (!id) {
        console.error("Fetch aborted: No customerId provided.");
        return;
      }

      console.log(`Fetching menu for customerId: ${id}...`);
      setIsRefreshing(true);

      // ── Step 1: Show localStorage instantly so the screen is never empty ──
      const cached = localStorage.getItem(`ai_menu_${id}`);
      if (cached) {
        try {
          const cachedSections = ensureItemsWithVisibility(JSON.parse(cached));
          setSections(cachedSections);
          if (cachedSections.length > 0) {
            // Keep the section the merchant is on if it still exists in the
            // reloaded menu; only fall back to the first one otherwise.
            setSelectedSectionId((prev) =>
              cachedSections.some((sec) => sec.id === prev) ? prev : cachedSections[0].id,
            );
          }
          setIsDataLoaded(true);
        } catch (_) {
          // corrupted cache — ignore, cloud will overwrite
        }
      }
      const cachedDesign = loadCachedDesignOptions(id);
      if (cachedDesign) {
        setDesignOptions({ ...DEFAULT_DESIGN_OPTIONS, ...cachedDesign });
      }

      // ── Step 2: Fetch from cloud in background (no loading spinner if cache hit) ──
      setIsLoading(!cached); // only show spinner when there's nothing cached yet
      try {
        const response = await axios.post(
          `${API_BASE}/getCustomerMenu`,
          { customerId: id },
          { timeout: 8000 },  // give up after 8 s and keep showing cached data
        );

        console.log("Fetch Response:", response.data);

        // A successful fetch means this store is no longer 404-ing — clear
        // its one-time reload flag so a future genuine 404 for this store
        // (e.g. menu removed later) still gets its single reload.
        sessionStorage.removeItem(`ai_menu_404_reloaded_${id}`);

        const menuData = response.data?.storeDetails;
        const menuWrapper = menuData?.menu || menuData;
        if (menuWrapper && menuWrapper.sections) {
          const fetchedSections = ensureItemsWithVisibility(menuWrapper.sections);
          const sectionsWithImages = await attachSavedItemImages(fetchedSections);
          setSections(sectionsWithImages);
          if (sectionsWithImages.length > 0) {
            setSelectedSectionId((prev) =>
              sectionsWithImages.some((sec) => sec.id === prev) ? prev : sectionsWithImages[0].id,
            );
          }
          if (menuData?.designOptions) {
            const mergedDesign = {
              ...DEFAULT_DESIGN_OPTIONS,
              ...menuData.designOptions,
            };
            setDesignOptions(mergedDesign);
            cacheDesignOptions(id, mergedDesign);
          }
          if (!hasShownCloudLoadedMessageRef.current) {
            message.success({
              content: "Menu synced from cloud.",
              key: "menu-loaded-cloud",
            });
            hasShownCloudLoadedMessageRef.current = true;
          }
        } else {
          // Store exists but has no menu saved yet (menu_data starts NULL
          // until the first Save) — a normal state, not a failure. Don't
          // leave a previous store's menu on screen if we have nothing
          // real cached for THIS store.
          console.log("No menu saved yet for this store.");
          if (!cached) {
            setSections([]);
            setDesignOptions(DEFAULT_DESIGN_OPTIONS);
            message.info({
              content: "No menu found for this store yet. Add a section to get started.",
              key: "menu-empty",
            });
          }
        }
      } catch (err) {
        console.error("Fetch error details:", err);
        // 404 means this store has no menu yet or the store doesn't exist —
        // do a hard reload so the page starts fresh for the new store context.
        // Guard against reload loops: a store with genuinely no menu will
        // 404 again right after the reload, so only reload ONCE per store
        // (tracked per customerId in sessionStorage). Switching to a
        // different store — even one that also has no menu — gets its own
        // fresh flag and reloads once for itself.
        if (err?.response?.status === 404) {
          const reloadFlagKey = `ai_menu_404_reloaded_${id}`;
          if (!sessionStorage.getItem(reloadFlagKey)) {
            sessionStorage.setItem(reloadFlagKey, "1");
            window.location.reload();
            return;
          }
          console.warn(
            `Already reloaded once for store ${id} after a 404; not reloading again.`,
          );
          if (!cached) {
            setSections([]);
            message.info({
              content: "No menu found for this store yet. Add a section to get started.",
              key: "menu-empty",
            });
          }
        } else if (!cached) {
          // Only warn if there was no cache to fall back on
          message.warning("No menu found for this store yet.");
        }
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
        setIsDataLoaded(true);
      }
    },
    [ensureItemsWithVisibility],
  );

  const syncMenuToServer = useCallback(
    async (updatedSections, designOpts = designOptions, options = {}) => {
      const { force = false, customerIdOverride = null } = options;
      const resolvedCustomerId = customerIdOverride || customerId;
      if (!resolvedCustomerId || (!isDataLoaded && !force)) return;

      setIsSyncing(true);
      const totalItems = updatedSections.reduce(
        (sum, s) => sum + (s.items?.length || 0),
        0,
      );

      let safeDesignOpts = sanitizeDesignOptionsForSave(designOpts);
      // item.image never goes into menu_data -- it lives in product_upc_images
      // (see attachSavedItemImages / handleSetItemImage) and is re-attached
      // client-side on every load instead. Stripped here so it can't sneak
      // back in via a `sections` array that still carries it in memory.
      const stripImage = (items) => (items || []).map(({ image, ...rest }) => rest);
      const payloadProbe = JSON.stringify({
        menu: {
          menu: {
            sections: (updatedSections || []).map((s) => ({
              id: s.id,
              name: s.name,
              items: stripImage(s.items),
            })),
          },
          designOptions: safeDesignOpts,
        },
      });
      if (payloadProbe.length > 60000 && safeDesignOpts.backgroundImageThumb) {
        safeDesignOpts = { ...safeDesignOpts, backgroundImageThumb: undefined };
      }
      console.log("[AIMenuScreen] syncMenuToServer", {
        thumbIncluded: !!safeDesignOpts.backgroundImageThumb,
        thumbLen: safeDesignOpts.backgroundImageThumb?.length,
        backgroundUrl: safeDesignOpts.backgroundImageUrl,
      });
      const payload = {
        customerId: resolvedCustomerId,
        menu: {
          menu: {
            sections: updatedSections.map((s) => ({
              id: s.id,
              name: s.name,
              items: stripImage(s.items),
            })),
          },
          metadata: {
            generatedAt: new Date().toISOString(),
            totalSections: updatedSections.length,
            totalItems: totalItems,
          },
          designOptions: safeDesignOpts,
        },
      };
      console.log("Payload:", payload);

      try {
        console.log("Syncing payload:", payload);
        await axios.post(`${API_BASE}/updateCustomerMenu`, payload);
        localStorage.setItem(`ai_menu_${resolvedCustomerId}`, JSON.stringify(updatedSections));
        cacheDesignOptions(resolvedCustomerId, designOpts);
        message.success({
          content: "Menu saved successfully.",
          key: "menu-saved-success",
        });
      } catch (err) {
        console.error("Sync Error:", err);
        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unknown error";
        message.error({
          content: `Sync failed: ${backendMessage}`,
          key: "menu-sync-failed",
        });
      } finally {
        setIsSyncing(false);
      }
    },
    [customerId, designOptions, isDataLoaded],
  );

  syncMenuToServerRef.current = syncMenuToServer;
  designOptionsRef.current = designOptions;

  // Auth redirect only — runs once per mount. Admins in Merchant Mode are
  // authenticated via admin_token + customer_id, not a merchant phone
  // number, so they're exempt from this check (the selected merchant may
  // not even have a mobile number on file).
  useEffect(() => {
    const phone = localStorage.getItem("phone");
    if (!phone && !isAdminMerchantMode()) {
      console.log("No phone found, redirecting to login...");
      navigate("/");
    }
  }, [navigate]);

  // Merge extracted sections from /extract-data navigation (once per location.key)
  useEffect(() => {
    const extractedSections = location.state?.sections;
    if (!Array.isArray(extractedSections)) return;
    if (extractMergeKeyRef.current === location.key) return;
    // Wait for the active store to resolve before doing anything — don't
    // mark this location.key as handled until we actually have a customerId.
    if (loadingStores) return;
    if (!customerId) return;
    extractMergeKeyRef.current = location.key;

    const baseSectionsFromState = location.state?.baseSections;

    setExtractedSectionsOnly(extractedSections);

    const loadAndMerge = async () => {
      let existingSections = Array.isArray(baseSectionsFromState)
        ? ensureItemsWithVisibility(baseSectionsFromState)
        : [];
      try {
        if (!existingSections.length) {
          const response = await axios.post(`${API_BASE}/getCustomerMenu`, {
            customerId,
          });
          const menuData = response.data?.storeDetails;
          const menuWrapper = menuData?.menu || menuData;
          existingSections = Array.isArray(menuWrapper?.sections)
            ? ensureItemsWithVisibility(menuWrapper.sections)
            : [];
          if (menuData?.designOptions) {
            const mergedDesign = {
              ...DEFAULT_DESIGN_OPTIONS,
              ...menuData.designOptions,
            };
            setDesignOptions(mergedDesign);
            cacheDesignOptions(customerId, mergedDesign);
          }
        }
      } catch (err) {
        console.error("Merge load error:", err);
        if (!existingSections.length) {
          const raw = localStorage.getItem(`ai_menu_${customerId}`);
          existingSections = raw ? ensureItemsWithVisibility(JSON.parse(raw)) : [];
        }
      }

      const mergedSections = mergeExtractedIntoSections(
        existingSections,
        extractedSections,
      );
      const existingNames = new Set(
        (existingSections || []).map((section) =>
          normalizeSectionName(section?.name),
        ),
      );
      const firstCreatedSectionName = (extractedSections || []).find(
        (section) => !existingNames.has(normalizeSectionName(section?.name)),
      )?.name;
      const firstCreatedSectionId = firstCreatedSectionName
        ? mergedSections.find(
            (section) =>
              normalizeSectionName(section?.name) ===
              normalizeSectionName(firstCreatedSectionName),
          )?.id
        : null;
      const firstMergedTargetSectionId = getFirstMergedTargetSectionId(
        mergedSections,
        extractedSections,
      );
      const mergedSectionsWithImages = await attachSavedItemImages(mergedSections);
      setSections(mergedSectionsWithImages);
      setSelectedSectionId(
        firstCreatedSectionId ||
          firstMergedTargetSectionId ||
          mergedSectionsWithImages[0]?.id ||
          null,
      );
      setIsDataLoaded(true);

      if (syncMenuToServerRef.current) {
        await syncMenuToServerRef.current(mergedSectionsWithImages, designOptionsRef.current, {
          force: true,
          customerIdOverride: customerId,
        });
      }

      skipNextFetchRef.current = true;
      message.success({
        content: "Extracted data merged into sections.",
        key: "extract-data-merged",
      });
      navigate("/ai-menu", { replace: true, state: null });
    };

    loadAndMerge();
  }, [
    navigate,
    location.key,
    location.state,
    mergeExtractedIntoSections,
    ensureItemsWithVisibility,
    getFirstMergedTargetSectionId,
    loadingStores,
    customerId,
  ]);

  // Load menu from cloud — once when the active store's customer_id is
  // available (not after extract merge, which loads/merges it separately above)
  useEffect(() => {
    const phone = localStorage.getItem("phone");
    const extractedSections = location.state?.sections;

    if (!phone) return;

    if (Array.isArray(extractedSections)) return;

    if (loadingStores) return; // still resolving the active store

    if (!customerId) {
      console.warn("No active store found for this account.");
      setIsDataLoaded(true);
      return;
    }

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    // Only auto-fetch once per store. The effect also re-runs whenever
    // loadingStores flips (the store list refetching in the background);
    // without this guard that re-fetched the menu mid-edit.
    if (lastAutoFetchedCustomerIdRef.current === customerId) return;
    lastAutoFetchedCustomerIdRef.current = customerId;

    fetchCustomerMenu(customerId);
  }, [fetchCustomerMenu, location.state?.sections, loadingStores, customerId]);

  const handleSave = useCallback(async () => {
    if (!customerId || !isDataLoaded) {
      message.warning("Please wait for menu to load.");
      return;
    }
    await syncMenuToServer(sections, designOptions);
  }, [customerId, designOptions, isDataLoaded, sections, syncMenuToServer]);

  const handleApplyExtractedData = useCallback(async () => {
    if (!extractedSectionsOnly.length) {
      message.warning("No extracted data found.");
      return;
    }

    const updatedSections = mergeExtractedIntoSections(sections, extractedSectionsOnly);
    const existingNames = new Set(
      sections.map((section) => normalizeSectionName(section?.name)),
    );
    const createdCount = extractedSectionsOnly.filter(
      (section) => !existingNames.has(normalizeSectionName(section?.name)),
    ).length;

    setSections(updatedSections);
    const mergedTargetSectionId = getFirstMergedTargetSectionId(
      updatedSections,
      extractedSectionsOnly,
    );
    setSelectedSectionId(
      mergedTargetSectionId || selectedSectionId || updatedSections[0]?.id || null,
    );

    await syncMenuToServer(updatedSections, designOptions);
    message.success({
      content: `Data applied. Added to existing sections and created ${createdCount} new section(s).`,
      key: "extract-data-applied",
    });
  }, [
    designOptions,
    extractedSectionsOnly,
    getFirstMergedTargetSectionId,
    mergeExtractedIntoSections,
    sections,
    selectedSectionId,
    syncMenuToServer,
  ]);

  const convertToCSV = useCallback((sectionsData) => {
    const escapeCSV = (value) => {
      const str = String(value ?? "");
      if (str.includes('"') || str.includes(",") || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = ["Section,Name,Price,Description"];
    (sectionsData || []).forEach((section) => {
      (section.items || []).forEach((item) => {
        rows.push(
          [
            escapeCSV(section?.name),
            escapeCSV(item?.name),
            escapeCSV(item?.price ?? "0.00"),
            escapeCSV(item?.description ?? ""),
          ].join(","),
        );
      });
    });
    return rows.join("\n");
  }, []);

  const previewJson = useMemo(() => JSON.stringify({ sections }, null, 2), [sections]);
  const dataJsonSourceSections = useMemo(
    () => (extractedSectionsOnly.length ? extractedSectionsOnly : []),
    [extractedSectionsOnly],
  );
  const previewDataJson = useMemo(
    () => JSON.stringify({ sections: dataJsonSourceSections }, null, 2),
    [dataJsonSourceSections],
  );
  const csvSourceSections = useMemo(
    () => (extractedSectionsOnly.length ? extractedSectionsOnly : []),
    [extractedSectionsOnly],
  );
  const previewCsv = useMemo(
    () => convertToCSV(csvSourceSections),
    [csvSourceSections, convertToCSV],
  );

  useEffect(() => {
    const handleSidebarToggle = (e) => setIsSidebarCollapsed(e.detail.isCollapsed);
    window.addEventListener("sidebarToggle", handleSidebarToggle);
    return () => window.removeEventListener("sidebarToggle", handleSidebarToggle);
  }, []);

  // Note: switching the active store already re-fetches in place — the
  // "Load menu from cloud" effect above depends on `customerId`, which
  // comes straight from useMerchantStores and changes reactively on
  // switch. No separate event listener needed here.

  const handleSectionSubmit = useCallback(
    (values) => {
      const trimmedName = values.name.trim();
      const updated = editingSectionId
        ? sections.map((s) =>
            s.id === editingSectionId ? { ...s, name: trimmedName } : s,
          )
        : [
            ...sections,
            {
              id: Date.now().toString(),
              name: trimmedName,
              items: [],
            },
          ];
      setSections(updated);
      if (editingSectionId) {
        setSelectedSectionId(editingSectionId);
      } else if (!selectedSectionId) {
        setSelectedSectionId(updated[updated.length - 1]?.id || null);
      }
      setIsSectionModalOpen(false);
      setEditingSectionId(null);
      syncMenuToServer(updated);
    },
    [editingSectionId, sections, selectedSectionId, syncMenuToServer],
  );

  const handleDeleteSection = useCallback(
    (id) => {
      const updated = sections.filter((s) => s.id !== id);
      setSections(updated);
      if (selectedSectionId === id) setSelectedSectionId(updated[0]?.id || null);
      syncMenuToServer(updated);
    },
    [sections, selectedSectionId, syncMenuToServer],
  );

  /** Duplicate an entire section — new section id + name "(Copy)", every
   * item inside gets its own fresh id so it's a real independent copy. */
  const handleDuplicateSection = useCallback(
    (id) => {
      const source = sections.find((s) => s.id === id);
      if (!source) return;

      const duplicatedSection = {
        ...source,
        id: createLocalId(),
        name: `${source.name || ""} (Copy)`,
        items: (source.items || []).map((item) => ({
          ...item,
          id: createLocalId(),
        })),
      };

      const sourceIndex = sections.findIndex((s) => s.id === id);
      const updated = [
        ...sections.slice(0, sourceIndex + 1),
        duplicatedSection,
        ...sections.slice(sourceIndex + 1),
      ];
      setSections(updated);
      setSelectedSectionId(duplicatedSection.id);
      syncMenuToServer(updated);
    },
    [sections, syncMenuToServer],
  );

  const handleItemSubmit = useCallback(
    (values) => {
      // Edits are merged onto the existing item (variations, isFavourite and
      // any other fields the form doesn't own must survive an edit).
      let newItem;
      try {
        newItem = buildItemFromForm(editingItem, values, () => Date.now().toString());
      } catch (err) {
        message.error("Fix the highlighted variation fields before saving.");
        return false;
      }
      const updated = sections.map((s) =>
        s.id === selectedSectionId
          ? {
              ...s,
              items: editingItem
                ? s.items.map((i) => (i.id === editingItem.id ? newItem : i))
                : [...(s.items || []), newItem],
            }
          : s,
      );
      setSections(updated);
      setIsItemModalOpen(false);
      syncMenuToServer(updated);
      return true;
    },
    [editingItem, sections, selectedSectionId, syncMenuToServer],
  );

  const handleDeleteItem = useCallback(
    (itemId) => {
      const updated = sections.map((s) =>
        s.id === selectedSectionId
          ? { ...s, items: s.items.filter((i) => i.id !== itemId) }
          : s,
      );
      setSections(updated);
      syncMenuToServer(updated);
    },
    [sections, selectedSectionId, syncMenuToServer],
  );

  const handleToggleItemVisibility = useCallback(
    (itemId, checked) => {
      const updated = sections.map((section) =>
        section.id === selectedSectionId
          ? {
              ...section,
              items: (section.items || []).map((item) =>
                item.id === itemId ? { ...item, isVisible: checked } : item,
              ),
            }
          : section,
      );
      setSections(updated);
      syncMenuToServer(updated);
    },
    [sections, selectedSectionId, syncMenuToServer],
  );

  // Favourite flag lives on the item itself inside menu_data (item.isFavourite),
  // same storage as everything else on an AI Menu item — persisted the same
  // way as visibility, via syncMenuToServer -> POST /updateCustomerMenu.
  const handleToggleItemFavourite = useCallback(
    (itemId, checked) => {
      const updated = sections.map((section) =>
        section.id === selectedSectionId
          ? {
              ...section,
              items: (section.items || []).map((item) =>
                item.id === itemId ? { ...item, isFavourite: checked } : item,
              ),
            }
          : section,
      );
      setSections(updated);
      syncMenuToServer(updated);
    },
    [sections, selectedSectionId, syncMenuToServer],
  );

  // AI-generated item image (item.image, a URL). Called after the merchant
  // reviews a freshly generated image and chooses "Save" (imageUrl = the new
  // URL), or removes an already-saved one (imageUrl = null). Persisted in
  // product_upc_images (mysqlDB.saveAiItemImage/removeAiItemImage) -- NOT in
  // menu_data -- so local state is only updated once that call succeeds; a
  // failed save/remove leaves the item exactly as it was rather than
  // silently drifting from what's actually stored server-side. The
  // generation call itself lives in imageCreationService.generateItemImage
  // and is triggered from AIMenuScreen -- this only persists the result.
  const handleSetItemImage = useCallback(
    async (itemId, imageUrl) => {
      try {
        if (imageUrl) {
          await saveItemImageRequest({ itemId, imageUrl });
        } else {
          await removeItemImageRequest({ itemId });
        }
      } catch (err) {
        console.error("[useAIMenu] handleSetItemImage failed:", err.message);
        message.error({
          content: `Couldn't ${imageUrl ? "save" : "remove"} the image: ${err.message || "Unknown error"}`,
          key: "item-image-save-failed",
        });
        return;
      }
      setSections((prev) =>
        prev.map((section) =>
          section.id === selectedSectionId
            ? {
                ...section,
                items: (section.items || []).map((item) =>
                  item.id === itemId ? { ...item, image: imageUrl } : item,
                ),
              }
            : section,
        ),
      );
    },
    [selectedSectionId],
  );

  // Photo / video taken or chosen on the device (AI Menu card upload button). Uploaded to S3 and
  // saved like a generated image; local state is updated only after the server confirms.
  // Resolves to an error message (string) on failure, or null on success.
  const handleUploadItemMedia = useCallback(
    async (itemId, file) => {
      try {
        const { url } = await uploadItemMediaRequest({ itemId, file });
        setSections((prev) =>
          prev.map((section) =>
            section.id === selectedSectionId
              ? { ...section, items: (section.items || []).map((item) => (item.id === itemId ? { ...item, image: url } : item)) }
              : section,
          ),
        );
        return null;
      } catch (err) {
        console.error("[useAIMenu] handleUploadItemMedia failed:", err.message);
        return err.message || "Couldn't upload the file. Please try again.";
      }
    },
    [selectedSectionId],
  );

  const currentSection = sections.find((s) => s.id === selectedSectionId) || null;

  const visibleSectionsForPreview = useMemo(
    () =>
      (sections || []).map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => item?.isVisible !== false),
      })),
    [sections],
  );


  const navigateToExtractData = useCallback(() => {
    navigate("/extract-data", { state: { currentSections: sections } });
  }, [navigate, sections]);

  return {
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    sections,
    setSections,
    selectedSectionId,
    setSelectedSectionId,
    isSectionModalOpen,
    setIsSectionModalOpen,
    editingSectionId,
    setEditingSectionId,
    isItemModalOpen,
    setIsItemModalOpen,
    isJsonPreviewOpen,
    setIsJsonPreviewOpen,
    isLoading,
    isRefreshing,
    isSyncing,
    isDataLoaded,
    editingItem,
    setEditingItem,
    customerId,
    showEditorMobile,
    setShowEditorMobile,
    designOptions,
    setDesignOptions,
    previewMode,
    setPreviewMode,
    extractedSectionsOnly,
    setExtractedSectionsOnly,
    currentSection,
    visibleSectionsForPreview,
    previewJson,
    previewDataJson,
    previewCsv,
    normalizeSectionName,
    itemDedupKey,
    ensureItemsWithVisibility,
    mergeExtractedIntoSections,
    getFirstMergedTargetSectionId,
    fetchCustomerMenu,
    syncMenuToServer,
    handleSave,
    handleApplyExtractedData,
    formatPrice,
    extractPriceHeading,
    stripPriceHeadingFromDescription,
    convertToCSV,
    handleSectionSubmit,
    handleDeleteSection,
    handleDuplicateSection,
    handleItemSubmit,
    handleDeleteItem,
    handleToggleItemVisibility,
    handleToggleItemFavourite,
    handleSetItemImage,
    handleUploadItemMedia,
    navigateToExtractData,
  };
};

export default useAIMenu;